import React, { useEffect, useState } from 'react';
import {
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Box,
  Chip,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import api from '../utils/api';
import { API } from '../config/apiConfig';
import AdminPageWrapper from './components/AdminPageWrapper';
import ProfessionalTable from '../components/ProfessionalTable';

function AdminUsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  
  // Detect if we're in admin or director context
  const isDirectorContext = location.pathname.startsWith('/director');
  const basePath = isDirectorContext ? '/director' : '/admin';

  const fetchUsers = async (term) => {
    if (!term || term.trim() === '') {
      setUsers([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const res = await api.get(API.adminUsers, {
        params: { searchTerm: term.trim() },
      });
      let fetchedUsers = res.data.users || [];
      
      // Filter out inactive users for directors
      if (isDirectorContext) {
        fetchedUsers = fetchedUsers.filter(user => user.IsActive === true || user.IsActive === 1);
      }
      
      setUsers(fetchedUsers);
    } catch (err) {
      console.error('❌ Failed to load users:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.message) {
      setSnackbarMsg(location.state.message);
      setSnackbarOpen(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.key]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSearch = () => {
    fetchUsers(searchTerm);
  };

  return (
    <AdminPageWrapper 
      title="Users List" 
      subtitle="Manage and view all registered users"
    >
      {/* Search box + button */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by username, email, etc."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          sx={(theme) => ({
            '& .MuiOutlinedInput-root': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(10px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid rgba(255,255,255,0.2)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(102, 126, 234, 0.5)',
              },
              '&.Mui-focused': {
                borderColor: '#4FACFE',
                boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
              },
            },
          })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={!searchTerm || searchTerm.trim() === ''}
          sx={{
            background: 'linear-gradient(135deg, #4FACFE 0%, #6366F1 40%, #9333EA 72%, #E11D48 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
            },
            '&:disabled': {
              background: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
            transition: 'all 0.3s ease',
            px: 3,
          }}
        >
          Search
        </Button>
      </Stack>

      <ProfessionalTable
        columns={[
          {
            header: 'Username',
            accessor: 'UserName',
            render: (value, row) => (
              <Box component={Link} to={`${basePath}/user-details/${row.UserID}`} sx={{ textDecoration: 'none', color: 'inherit' }}>
                <Typography fontWeight={500}>{value}</Typography>
              </Box>
            )
          },
          {
            header: 'Name',
            accessor: 'FirstName',
            render: (value, row) => (
              <Box>
                <Typography>{value || '—'}</Typography>
                <Typography variant="body2" color="text.secondary">{row.LastName || '—'}</Typography>
              </Box>
            )
          },
          {
            header: 'Email',
            accessor: 'Email',
            render: (email) => (
              <Typography variant="body2">{email}</Typography>
            )
          },
          {
            header: 'Mobile',
            accessor: 'MobileNumber',
            render: (mobile) => (
              <Typography variant="body2">{mobile || '—'}</Typography>
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Role',
            accessor: 'UserTypeName',
            render: (role) => (
              <Chip label={role} size="small" color="primary" variant="outlined" />
            ),
            style: { textAlign: 'center' }
          },
          {
            header: 'Status',
            accessor: 'IsActive',
            render: (isActive) => (
              <Chip 
                label={isActive ? 'Active' : 'Inactive'}
                color={isActive ? 'success' : 'error'}
                size="small"
              />
            ),
            style: { textAlign: 'center' }
          }
        ]}
        data={users.map(user => ({ ...user, id: user.UserID }))}
        onRowClick={(user) => navigate(`${basePath}/user-details/${user.UserID}`)}
        loading={loading}
        emptyMessage={hasSearched ? "No users found" : "Enter a search term to find users"}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={handleSnackbarClose}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </AdminPageWrapper>
  );
}

export default AdminUsersList;
